import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, User, Phone, MapPin, Calendar, Clock, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import axiosInstance from '../api/axiosInstance';
import InactivityTimer from '../components/InactivityTimer';
import { getPatientSession } from '../utils/patientSession';

const OnlineDeposit = () => {
    const navigate = useNavigate();
    const [patientData, setPatientData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [collectionType, setCollectionType] = useState('On Account');
    const [depositAmount, setDepositAmount] = useState('');
    const [showPaymentStatusModal, setShowPaymentStatusModal] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentMessage, setPaymentMessage] = useState('');
    const [showNonIPStatusDialog, setShowNonIPStatusDialog] = useState(false);
    const [isNonIPPatient, setIsNonIPPatient] = useState(false);
    const [visitDetails, setVisitDetails] = useState(null);

    const serviceTypes = {
        // bed: 117,
        lab: 115,
        imaging: 116,
        ot: 1012
    };

    const mapApiPatientToForm = (patient) => ({
        prefix: patient.prefix || '',
        firstName: patient.firstName || '',
        middleName: patient.middleName || '',
        lastName: patient.lastName || '',
        mrno: patient.mrno || '',
        genderCode: patient.genderCode || '',
        dob: patient.dobStr ? patient.dobStr.split('-').reverse().join('-') : '',
        age: patient.age || '',
        ageSelect: 'year',
        contactNumber: patient.contactNo || '',
        emailAddress: patient.email || '',
        emergencyContactName: patient.emergencyContactName || '',
        emergencyContactNo: patient.emergencyContactNo || patient.contactNo || '',
        city: patient.addressList?.[0]?.cityName || '',
        district: patient.addressList?.[0]?.districtName || '',
        state: patient.addressList?.[0]?.stateName || '',
        nationality: patient.addressList?.[0]?.countryName || '',
        addressList: patient.addressList || [],
        base64String: patient.photo || null
    });

    // Fetch patient visit details to check IP status
    const fetchPatientVisitDetails = async (patientMrno) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(patientMrno)}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                },
            });

            if (response.data && response.data.status === 'success' && response.data.data && response.data.data[0]) {
                const visitData = response.data.data[0].visitDetails;
                setVisitDetails(visitData);

                // Check if patient status does NOT start with 'IP' OR visitStatus is not 'open' - only IP patients with open visit can make deposits
                if (visitData && visitData.patVisitStatus && (!visitData.patVisitStatus.startsWith('IP') || visitData.visitStatus !== 'open')) {
                    setIsNonIPPatient(true);
                    setShowNonIPStatusDialog(true);
                } else {
                    setIsNonIPPatient(false);
                }
            }
        } catch (err) {
            console.error('Error fetching visit details:', err);
        }
    };

    const fetchPatientByInput = useCallback(async (input) => {
        if (!input) {
            setError('Logged-in patient details not found. Please login again.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(input)}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1') || ''}` }
            });
            if (response.data.status === 'success' && Array.isArray(response.data.data) && response.data.data.length > 0) {
                // Prefer exact MRNO match when multiple profiles share a mobile
                const exact = response.data.data.find((p) => String(p.mrno) === String(input));
                const patient = exact || response.data.data[0];
                setPatientData(mapApiPatientToForm(patient));
                await fetchPatientVisitDetails(patient.mrno);
            } else {
                setError('No patient found for the logged-in profile');
                setPatientData(null);
            }
        } catch (err) {
            setError('Failed to fetch patient details');
            setPatientData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const session = getPatientSession();
        if (!session?.mrno) {
            setError('Please login to continue with advance payment.');
            return;
        }
        fetchPatientByInput(session.mrno);
    }, [fetchPatientByInput]);

    useEffect(() => {
        // Cart total calculation - deprecated
    }, []);

    const removeFromCart = (serviceId) => {
        // Deprecated
    };

    const updateQuantity = (serviceId, delta) => {
        // Deprecated
    };

    const handlePayment = async () => {
        // Prevent payment if patient is NOT IP
        if (isNonIPPatient) {
            setShowNonIPStatusDialog(true);
            return;
        }

        if (!depositAmount || parseFloat(depositAmount) <= 0) {
            setPaymentStatus('failure');
            setPaymentMessage('Please enter a valid deposit amount.');
            setShowPaymentStatusModal(true);
            return;
        }

        setLoading(true);
        try {
            // Create collection request
            const response = await axiosInstance.post('/api/patient/create-collection-order', {
                patientMrno: patientData.mrno,
                patientName: `${patientData.prefix || ''} ${patientData.firstName} ${patientData.middleName || ''} ${patientData.lastName}`.trim(),
                patientMobile: patientData.contactNumber,
                collectionType: collectionType,
                amount: parseFloat(depositAmount),
                siteCode: localStorage.getItem('site_code') || null,
            }, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('kiosk_token')}` }
            });

            const { key, amount, currency, order_id } = response.data;

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://api.razorpay.com/v1/checkout/embedded';

            const formFields = {
                key_id: key,
                amount: amount.toString(),
                currency,
                order_id,
                name: "KIMS",
                description: "Online Deposit - " + collectionType,
                image: "/logo.jpg",
                callback_url: `${axiosInstance.defaults.baseURL}/api/patient/verify-collection-payment`,
                cancel_url: `${axiosInstance.defaults.baseURL}/api/patient/cancel-payment`,
                prefill: {
                    name: `${patientData.firstName || ''}  ${patientData.middleName || ''} ${patientData.lastName || ''}`.trim() || 'Test User',
                    email: patientData.emailAddress || 'test@gmail.com',
                    contact: patientData.contactNumber || '9427388748',
                },
                theme: { color: "#0FA968" },
                notes: {
                    collection_type: collectionType,
                    patient_mrno: patientData.mrno,
                },
            };

            Object.keys(formFields).forEach((key) => {
                if (typeof formFields[key] === 'object') {
                    Object.keys(formFields[key]).forEach((subKey) => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = `${key}[${subKey}]`;
                        input.value = formFields[key][subKey];
                        form.appendChild(input);
                    });
                } else {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = formFields[key];
                    form.appendChild(input);
                }
            });

            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);
        } catch (err) {
            setPaymentStatus('failure');
            setPaymentMessage('Error creating collection request: ' + (err.response?.data?.error || err.message || 'Unknown error'));
            setShowPaymentStatusModal(true);
            console.error('Error creating order:', err);
        } finally {
            setLoading(false);
        }
    };

    const allServices = [];

    const displayedServices = [];

    const renderPaymentStatusModal = () => {
        if (!paymentStatus || paymentStatus === 'success') return null;
        const statusConfig = {
            failure: { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: "❌", title: "Payment Failed" },
            cancel: { bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-800", icon: "⚠️", title: "Payment Cancelled" }
        };
        const config = statusConfig[paymentStatus];
        return (
            <div className="fixed inset-0 z-50 flex justify-center w-full h-full bg-black bg-opacity-50 overflow-y-auto px-4 py-4">
                <div className="relative my-auto p-4 sm:p-6 w-full max-w-lg bg-white rounded-2xl shadow-2xl">
                    <button
                        onClick={() => setShowPaymentStatusModal(false)}
                        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div className={`p-6 border-2 rounded-xl ${config.bg} ${config.border} text-center`}>
                        <span className="text-4xl mb-4 block">{config.icon}</span>
                        <h3 className={`font-bold text-xl ${config.text}`}>{config.title}</h3>
                        <p className={`mt-2 ${config.text} opacity-90`}>{paymentMessage}</p>
                        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                            <button
                                onClick={() => {
                                    setShowPaymentStatusModal(false);
                                    setPaymentStatus(null);
                                    setPaymentMessage("");
                                }}
                                className="w-full sm:w-auto px-6 py-2 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => setShowPaymentStatusModal(false)}
                                className="w-full sm:w-auto px-6 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg shadow hover:bg-gray-300 transition duration-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 overflow-x-hidden">
                <div className="bg-white shadow-sm border-b">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-wrap items-center justify-between gap-3 py-3 sm:py-4">
                            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                                <Link to="/dashboard" className="inline-flex items-center px-3 sm:px-4 py-2 bg-emerald-600 text-white text-sm sm:text-base font-medium rounded-lg hover:bg-emerald-700 transition-colors duration-200 shadow-sm">
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Back
                                </Link>
                                <div className="hidden sm:block w-px h-8 bg-gray-300"></div>
                                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Advance Payment</h1>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
                    {loading && !patientData && (
                        <div className="flex justify-center items-center py-16 min-h-[40vh]">
                            <div className="flex items-center gap-3 text-emerald-700">
                                <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                <span className="font-medium">Loading your patient details...</span>
                            </div>
                        </div>
                    )}

                    {!loading && !patientData && error && (
                        <div className="flex justify-center items-center py-8 sm:py-16 min-h-[40vh]">
                            <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6 w-full max-w-xl">
                                <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                                    <p className="text-red-700 text-sm">{error}</p>
                                </div>
                                <div className="mt-4 flex justify-center">
                                    <Link
                                        to="/dashboard"
                                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                                    >
                                        Back to Dashboard
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}

                    {patientData && (
                        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
                            <div className="w-full lg:w-[70%]">
                                <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-4 sm:mb-6">
                                    <div className="bg-emerald-50 border-b border-emerald-100 px-4 sm:px-6 py-3">
                                        <div className="flex items-start sm:items-center">
                                            <CheckCircle className="w-5 h-5 text-emerald-600 mr-2 sm:mr-3 shrink-0 mt-0.5 sm:mt-0" />
                                            <p className="text-emerald-800 font-medium text-sm sm:text-base">Logged-in Patient Details</p>
                                        </div>
                                    </div>
                                    <div className="p-3 sm:p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:space-x-4 min-w-0">
                                                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center shrink-0">
                                                    {patientData.base64String ? (
                                                        <img
                                                            src={patientData.base64String}
                                                            alt="Patient"
                                                            className="w-full h-full object-cover rounded-xl"
                                                        />
                                                    ) : (
                                                        <User className="w-8 h-8 text-emerald-600" />
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 break-words">
                                                        {`${patientData.prefix || ''} ${patientData.firstName} ${patientData.middleName || ''} ${patientData.lastName}`.trim()}
                                                    </h3>
                                                    <p className="text-xs text-gray-500">MRNO: {patientData.mrno}</p>
                                                </div>
                                            </div>
                                            <div className="space-y-2 min-w-0">
                                                <div className="flex items-start sm:items-center">
                                                    <Calendar className="w-4 h-4 text-gray-400 mr-2 shrink-0 mt-0.5 sm:mt-0" />
                                                    <div className="min-w-0">
                                                        <p className="text-xs text-gray-500">DOB / Age</p>
                                                        <p className="text-sm font-medium text-gray-900 break-words">
                                                            {patientData.dob} ({patientData.age} {patientData.ageSelect || 'year'}(s))
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-start sm:items-center">
                                                    <Phone className="w-4 h-4 text-gray-400 mr-2 shrink-0 mt-0.5 sm:mt-0" />
                                                    <div className="min-w-0">
                                                        <p className="text-xs text-gray-500">Mobile</p>
                                                        <p className="text-sm font-medium text-gray-900">{patientData.contactNumber}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-2 min-w-0 md:col-span-2 xl:col-span-1">
                                                <div className="flex items-start sm:items-center">
                                                    <MapPin className="w-4 h-4 text-gray-400 mr-2 shrink-0 mt-0.5 sm:mt-0" />
                                                    <div className="min-w-0">
                                                        <p className="text-xs text-gray-500">Address</p>
                                                        <p className="text-sm font-medium text-gray-900 break-words">
                                                            {`${patientData.addressList?.[0]?.address || ''}, ${patientData.city}, ${patientData.state}`.trim()}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-start sm:items-center">
                                                    <Users className="w-4 h-4 text-gray-400 mr-2 shrink-0 mt-0.5 sm:mt-0" />
                                                    <div className="min-w-0">
                                                        <p className="text-xs text-gray-500">Emergency Contact</p>
                                                        <p className="text-sm font-medium text-gray-900 break-words">
                                                            {patientData.emergencyContactName} ({patientData.emergencyContactNo})
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
                                    <div className="mb-4 sm:mb-6">
                                        <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Add Collection Request</h2>
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label htmlFor="collectionType" className="block text-sm font-medium text-gray-700 mb-2">
                                                        Collection Type
                                                    </label>
                                                    <select
                                                        id="collectionType"
                                                        value={collectionType}
                                                        disabled
                                                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                                                    >
                                                        <option value="On Account">On Account</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label htmlFor="depositAmount" className="block text-sm font-medium text-gray-700 mb-2">
                                                        Amount
                                                    </label>
                                                    <input
                                                        type="number"
                                                        id="depositAmount"
                                                        value={depositAmount}
                                                        onChange={(e) => setDepositAmount(e.target.value)}
                                                        placeholder="Enter amount"
                                                        min="0"
                                                        step="0.01"
                                                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="w-full lg:w-[30%]">
                                <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6 lg:sticky lg:top-6">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                                        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Collection Summary</h3>
                                        <div className="space-y-3">
                                            <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                                                <span className="text-gray-600 text-sm sm:text-base">Collection Type:</span>
                                                <span className="font-semibold text-gray-900 text-sm sm:text-base">{collectionType}</span>
                                            </div>
                                            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 border-t pt-3">
                                                <span className="text-gray-600 text-sm sm:text-base">Amount:</span>
                                                <span className="text-base sm:text-lg font-bold text-emerald-600">
                                                    ₹{depositAmount ? parseFloat(depositAmount).toFixed(2) : '0.00'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <button
                                            onClick={handlePayment}
                                            className={`w-full py-3 bg-emerald-600 text-white font-semibold rounded-lg shadow hover:bg-emerald-700 transition duration-200 ${!depositAmount || parseFloat(depositAmount) <= 0 || loading ? 'opacity-50 cursor-not-allowed' : ''
                                                }`}
                                            disabled={!depositAmount || parseFloat(depositAmount) <= 0 || loading}
                                        >
                                            {loading ? (
                                                <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                                            ) : (
                                                'Proceed'
                                            )}
                                        </button>
                                        <button
                                            onClick={() => navigate('/dashboard')}
                                            className="w-full py-3 bg-gray-300 text-gray-800 font-semibold rounded-lg shadow hover:bg-gray-400 transition duration-200"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {renderPaymentStatusModal()}
                </div>
            </div>

            {/* Non-IP Status Dialog */}
            {showNonIPStatusDialog && (
                <div className="fixed inset-0 z-50 flex justify-center w-full h-full bg-black bg-opacity-50 px-4 overflow-y-auto">
                    <div className="relative p-5 sm:p-8 w-full max-w-md bg-white rounded-2xl shadow-2xl my-auto py-4">
                        <div className="text-center">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                                <svg className="w-10 h-10 sm:w-12 sm:h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className="text-xl sm:text-2xl font-bold text-[#22292A] mb-3 sm:mb-4">
                                Cannot Process Deposit
                            </h3>
                            <p className="text-gray-600 text-sm sm:text-lg leading-relaxed mb-4">
                                {!visitDetails?.patVisitStatus?.startsWith('IP') ? (
                                    <>Deposits can only be made for In-Patient (IP) visits. Your current visit status is <strong className="text-red-600">{visitDetails?.patVisitStatus}</strong>.</>
                                ) : (
                                    <>Deposits can only be made for visits with <strong>open</strong> status. Your current visit status is <strong className="text-red-600">{visitDetails?.visitStatus}</strong>.</>
                                )}
                            </p>
                            <div className="bg-red-50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 text-left">
                                <p className="text-xs sm:text-sm text-gray-700 break-words">
                                    <strong>Patient Visit Status:</strong> {visitDetails?.patVisitStatus}<br />
                                    <strong>Visit Status:</strong> {visitDetails?.visitStatus}<br />
                                    <strong>Last Visit:</strong> {visitDetails?.lastVisitNo}<br />
                                    <strong>Visit Date:</strong> {visitDetails?.visitDate}
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className="flex-1 bg-[#0FA968] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#138F5E] transition duration-200 text-sm sm:text-base"
                                >
                                    Go to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OnlineDeposit;